import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IncomingGeneralContent, IncomingTransactionGeneral } from '@ui/src';
import { useAppSelector } from '@store';
import { useLazyGetCustomersAccountBalanceQuery } from '../store/services/customers';
import { Balance } from '../types';

const ProblemIncomingGeneralContentHOC = (): React.JSX.Element => {
  const [accountBalance, setAccountBalance] = useState<number>();
  const [pspPartnerAccountBalance, setPspPartnerAccountBalance] =
    useState<number>();
  const [balances, setBalances] = useState<Balance[]>();
  const [pspPartnerAccountBalanceInfo, setPspPartnerAccountBalanceInfo] =
    useState<Balance>();

  const {
    customersProblemTransactions: {
      incoming: { transaction },
    },
    customersDictionaries: {
      currenciesList,
      pspAccounts,
      paymentTypes,
      overdraftEnabled,
    },
  } = useAppSelector((state) => state);

  const { transactionId } = useParams();

  const [
    getAccountBalance,
    { isLoading: balanceIsLoading, isFetching: balanceIsFetching },
  ] = useLazyGetCustomersAccountBalanceQuery();
  const [
    getPspPartnerBalance,
    {
      isLoading: pspPartnerBalanceisLoading,
      isFetching: pspPartnerBalanceisFetching,
    },
  ] = useLazyGetCustomersAccountBalanceQuery();

  const getAccountBalanceAction = useCallback(async () => {
    const response = (await getAccountBalance({
      id: transaction?.recipientAccount.id,
      currencyId: transaction?.currencyId as string,
    })) as { data: { data: { edges: { node: Balance }[] } } };

    if (response.data) {
      setAccountBalance(response.data.data.edges[0].node.value);
      setBalances(response.data.data.edges?.map((item) => item.node));
    }

    const pspPartnerAccountId = pspAccounts?.find(
      (item) =>
        item.account?.fullNumber === transaction?.pspPartnerAccountNumber,
    )?.account?.id;

    if (pspPartnerAccountId) {
      const pspResponse = (await getPspPartnerBalance({
        id: pspPartnerAccountId,
        currencyId: transaction?.currencyId as string,
      })) as { data: { data: { edges: { node: Balance }[] } } };

      if (pspResponse.data) {
        setPspPartnerAccountBalance(pspResponse.data.data.edges[0].node.value);
        setPspPartnerAccountBalanceInfo(pspResponse.data.data.edges[0].node);
      }
    }
  }, [transaction]);

  useEffect(() => {
    if (transaction) {
      getAccountBalanceAction();
    }
  }, [transaction]);

  return (
    <IncomingGeneralContent
      general={transaction as IncomingTransactionGeneral}
      transactionUrlType="incoming"
      transactionId={transactionId}
      currenciesList={currenciesList}
      withTransactionUrl
      isLoading={
        balanceIsLoading ||
        pspPartnerBalanceisLoading ||
        balanceIsFetching ||
        pspPartnerBalanceisFetching
      }
      accountBalance={accountBalance}
      currencyName={transaction?.currency?.name}
      pspPartnerAccountBalance={pspPartnerAccountBalance}
      pspPartnerName={
        pspAccounts?.find(
          (item) => item.pspPartner.id === transaction?.pspPartnerId,
        )?.pspPartner.companyName
      }
      recipientAccounts={[
        {
          name: transaction?.recipientAccount?.description,
          value: transaction?.recipientAccount?.fullNumber,
          suffix: transaction?.recipientAccount?.fullNumber,
        },
      ]}
      paymentsTypes={paymentTypes?.map((item) => ({
        name: item.name,
        value: item.paymentType,
      }))}
      overdraftEnabled={overdraftEnabled}
      recipientAccountsWithInfo={[transaction?.recipientAccount]}
      balances={balances}
      currencies={currenciesList?.map((item) => ({
        name: item.name,
        value: item.id,
      }))}
      currencyId={transaction?.currencyId}
      pspPartnersAccountsList={pspAccounts}
      pspPartnerAccountBalanceInfo={pspPartnerAccountBalanceInfo}
    />
  );
};

export default ProblemIncomingGeneralContentHOC;
